import React, { useState, useEffect } from 'react';
import { Form, Button, message, Table, Space, Popconfirm, Tag, Progress, Card, Upload, Input, Switch, Divider, Tabs, Modal } from 'antd';
import { DeleteOutlined, EyeOutlined, ArrowLeftOutlined, SyncOutlined, UploadOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createTask, getTasksWithCurrent, deleteTask, cancelTask, TaskResponse, shouldDisplayInList } from '../../../utils/taskManager';
import { isProfileVerifyRequired } from '../../../api/taskManagerApi';
import { getVisitorId } from '../../../utils/session';
import MolStarViewer from '../../../components/results/protein/structure/MolStarViewer';
import { formatUTCToLocal } from '../../../utils/timeUtils';

const { TextArea } = Input;

interface LocalESMFoldResult {
  name: string;
  sequence: string;
  pdb_content: string;
  atoms: number;
  residues: number;
  success: boolean;
  error?: string;
}

export default function LocalESMFoldPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<TaskResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedResult, setSelectedResult] = useState<{title: string; results: LocalESMFoldResult[]; selectedIndex: number} | null>(null);
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [fastaContent, setFastaContent] = useState('');
  
  // 加载任务列表
  const loadTasks = async () => {
    setLoading(true);
    try {
      const visitorId = getVisitorId();
      // 获取ESMFold任务 - 使用handler_name过滤
      const taskList = await getTasksWithCurrent(0, 100, undefined, 'structure_prediction', undefined, 'esmfold_local_predict_json');
      console.log('获取到的本地ESMFold任务列表:', taskList);
      
      // 过滤掉已删除的任务
      const deletedTaskIds = JSON.parse(localStorage.getItem('deletedLocalESMFoldTaskIds') || '[]');
      const finalTasks = taskList.filter(task => !deletedTaskIds.includes(task.id));

      // 仅展示长时任务
      const displayTasks = finalTasks.filter(shouldDisplayInList);
      
      setTasks(displayTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
    } catch (error) {
      console.error('加载任务列表失败:', error);
      if (!isProfileVerifyRequired(error)) {
        message.error('加载任务列表失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 处理文件上传
  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setFastaContent(content);
      form.setFieldsValue({ fasta_content: content });
    };
    reader.readAsText(file);
    return false; // 阻止自动上传
  };

  // 提交预测任务
  const handleSubmit = async (values: any) => {
    if (!values.fasta_content || !values.fasta_content.trim()) {
      message.error('请输入FASTA序列或上传FASTA文件');
      return;
    }

    // 验证FASTA格式
    if (!values.fasta_content.includes('>')) {
      message.error('无效的FASTA格式，必须包含序列标题行（以>开头）');
      return;
    }

    setSubmitting(true);
    try {
      const taskData = {
        name: values.task_name || '本地ESMFold预测',
        description: `本地ESMFold结构预测 - ${values.task_name || '多序列预测'}`,
        service_name: 'structure_prediction',
          task_type: 'esmfold',
        handler_name: 'esmfold_local_predict_json',
        parameters: {
          fasta_content: values.fasta_content.trim(),
          task_name: values.task_name || null
        },
        priority: 1,
        timeout: 3600,
        max_retries: 1
      };

      const task = await createTask(taskData);
      message.success('任务已提交，正在处理中...');
      setTasks(previous => [task, ...previous.filter(item => item.id !== task.id)]);
      form.resetFields();
      setFastaContent('');
      loadTasks();
    } catch (error) {
      console.error('提交任务失败:', error);
      message.error('提交任务失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 删除任务
  const handleDelete = async (taskId: string) => {
    try {
      const deletedTasks = JSON.parse(localStorage.getItem('deletedLocalESMFoldTaskIds') || '[]');
      if (!deletedTasks.includes(taskId)) {
        deletedTasks.push(taskId);
        localStorage.setItem('deletedLocalESMFoldTaskIds', JSON.stringify(deletedTasks));
      }
      
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      message.success('任务已删除');
    } catch (error) {
      console.error('删除任务失败:', error);
      message.error('删除任务失败');
    }
  };

  const handleCancel = async (taskId: string) => {
    try {
      await cancelTask(taskId);
      message.success('任务已取消');
      loadTasks();
    } catch (error) {
      console.error('取消任务失败:', error);
      message.error('取消任务失败');
    }
  };

  // 解析pdb并统计原子与残基
  const countAtomsResidues = (pdb: string) => {
    if (!pdb) return { atoms: 0, residues: 0 };
    const lines = pdb.split('\n');
    let atomCount = 0;
    let residueCount = 0;
    const atomRegex = /^\s*ATOM/;
    const caRegex = /\sCA(\s|$)/; // 匹配列对齐情况下的 CA 原子名
    for (const line of lines) {
      if (atomRegex.test(line)) {
        atomCount += 1;
        if (caRegex.test(line)) residueCount += 1;
      }
    }
    return { atoms: atomCount, residues: residueCount };
  };

  // 下载所有PDB文件（逐个下载）
  const downloadAllPDB = (results: LocalESMFoldResult[], taskName: string) => {
    if (!results || results.length === 0) {
      message.warning('没有可下载的结构文件');
      return;
    }

    try {
      let downloadCount = 0;
      results.forEach((result, index) => {
        if (result.pdb_content) {
          setTimeout(() => {
            const blob = new Blob([result.pdb_content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${result.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdb`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            downloadCount++;
            
            if (downloadCount === results.filter(r => r.pdb_content).length) {
              message.success(`已下载 ${downloadCount} 个结构文件`);
            }
          }, index * 200); // 每个文件间隔200ms下载，避免浏览器阻止
        }
      });
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败');
    }
  };

  // 下载单个PDB文件
  const downloadSinglePDB = (result: LocalESMFoldResult) => {
    if (!result.pdb_content) {
      message.warning('该结构没有PDB数据');
      return;
    }

    try {
      const blob = new Blob([result.pdb_content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${result.name.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdb`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('PDB文件已下载');
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败');
    }
  };

  const handleViewResult = (task: TaskResponse) => {
    console.log('点击查看结果，任务数据:', task);
    if (task.result) {
      console.log('任务结果数据:', task.result);
      
      // 提取结构预测结果 - 修正数据结构解析
      let results: LocalESMFoldResult[] = [];
      
      // 聚合所有可能格式下的结果
      if (Array.isArray(task.result.results)) {
        for (const entry of task.result.results) {
          const payload = entry.result ?? entry; // 兼容 result 包裹或直接数据
          if (!payload) continue;

          // 1) payload.results: 多序列数组
          if (Array.isArray(payload.results)) {
            for (const item of payload.results) {
              const name = item.sequence_id || item.name || 'Unknown';
              const sequence = item.sequence || item.result?.sequence || '';
              const pdb = item.pdb_content || item.pdb_data || item.result?.pdb_content || item.result?.pdb_data || '';
              const counts = countAtomsResidues(pdb);
              const atoms = Number(item.atoms ?? item.result?.atoms ?? counts.atoms) || counts.atoms;
              const residues = Number(item.residues ?? item.result?.residues ?? counts.residues) || counts.residues;
              const success = typeof item.success === 'boolean' ? item.success : (item.result?.success !== false);
              const error = item.error || item.result?.error;
              results.push({ name, sequence, pdb_content: pdb, atoms, residues, success, error });
            }
          } else {
            // 2) 单个结果对象
            const name = payload.name || 'Protein';
            const sequence = payload.sequence || '';
            const pdb = payload.pdb_content || payload.pdb_data || '';
            if (pdb) {
              const counts = countAtomsResidues(pdb);
              const atoms = Number(payload.atoms ?? counts.atoms) || counts.atoms;
              const residues = Number(payload.residues ?? counts.residues) || counts.residues;
              const success = payload.success !== false;
              const error = payload.error;
              results.push({ name, sequence, pdb_content: pdb, atoms, residues, success, error });
            }
          }
        }
      }
      
      console.log('提取的结构结果数量:', results.length);
      console.log('结构结果详情:', results);
      
      if (results.length > 0) {
        setSelectedResult({
          title: `${task.name} - 预测结果`,
          results: results,
          selectedIndex: 0
        });
        setResultModalVisible(true);
      } else {
        message.warning('暂无有效的结构预测结果');
        console.warn('无法解析结果数据，原始结构:', task.result);
      }
    } else {
      task.result?.pdb_content && setSelectedResult({
        title: `${task.name} - 预测结果`,
        results: [{
          name: task.name || 'Protein',
          sequence: task.parameters?.fasta_content || '',
          pdb_content: task.result?.pdb_content,
          atoms: 0,
          residues: 0,
          success: true
        }],
        selectedIndex: 0
      });
      setResultModalVisible(true);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      'pending': { color: 'blue', text: '等待中' },
      'running': { color: 'orange', text: '运行中' },
      'completed': { color: 'green', text: '已完成' },
      'failed': { color: 'red', text: '失败' },
      'cancelled': { color: 'gray', text: '已取消' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress: number, record: TaskResponse) => {
        if (record.status === 'completed') return <Progress percent={100} size="small" />;
        if (record.status === 'failed' || record.status === 'cancelled') return <Progress percent={0} status="exception" size="small" />;
        return <Progress percent={progress || 0} size="small" />;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => formatUTCToLocal(time),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: TaskResponse) => (
        <Space size="middle">
          {record.status === 'completed' && (
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewResult(record)}
            >
              查看结果
            </Button>
          )}
          {(record.status === 'pending' || record.status === 'running') && (
            <Button 
              type="link" 
              onClick={() => handleCancel(record.id)}
            >
              取消
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个任务吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 10000); // 10秒刷新一次，本地预测较慢
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="local-esmfold-page">
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate('/structure')}
        style={{ marginBottom: 24 }}
      >
        返回结构与设计
      </Button>

      <Card title="ESMFold 蛋白质结构预测" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="task_name"
            label="任务名称"
          >
            <Input placeholder="输入任务名称（可选）" />
          </Form.Item>

          <Form.Item
            name="fasta_content"
            label="FASTA序列"
            rules={[{ required: true, message: '请输入FASTA序列' }]}
          >
            <TextArea
              rows={8}
              placeholder="请输入FASTA格式的序列，支持多序列：&#10;>Protein1&#10;MKLLVL...&#10;>Protein2&#10;QSVEESGGRLVT..."
              value={fastaContent}
              onChange={(e) => setFastaContent(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="或上传FASTA文件">
            <Upload
              beforeUpload={handleFileUpload}
              accept=".fasta,.fa,.txt"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>选择FASTA文件</Button>
            </Upload>
          </Form.Item>

          <Divider />

          {/* 处理块大小参数已移除，后端使用默认值 */}

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={submitting}
              size="large"
            >
              {submitting ? '提交中...' : '开始预测'}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card 
        title="任务列表" 
        extra={
          <Button 
            size="small" 
            onClick={loadTasks}
            loading={loading}
            icon={<SyncOutlined />}
          >
            刷新任务
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个任务`,
          }}
        />
      </Card>

      <Modal
        width={1200}
        centered
        destroyOnClose
        open={resultModalVisible && !!selectedResult}
        title={selectedResult?.title || '预测结果'}
        onCancel={() => {
          setResultModalVisible(false);
          setSelectedResult(null);
        }}
        bodyStyle={{ padding: 0, height: '80vh', overflow: 'hidden' }}
        footer={null}
      >
        {selectedResult && (
          <div style={{ 
            width: '100%', 
            height: '100%',
            display: 'flex',
            flexDirection: 'row'
          }}>
            {/* 左侧序列列表 */}
            {selectedResult.results.length > 1 && (
              <div style={{ 
                width: 280, 
                minWidth: 280,
                borderRight: '1px solid #f0f0f0', 
                display: 'flex',
                flexDirection: 'column',
                background: '#fafafa'
              }}>
                <div style={{ 
                  padding: '12px 16px', 
                  borderBottom: '1px solid #f0f0f0',
                  fontWeight: 600,
                  fontSize: 14
                }}>
                  序列列表 ({selectedResult.results.length})
                </div>
                <div style={{ 
                  flex: 1, 
                  overflow: 'auto',
                  padding: '8px 0'
                }}>
                  {selectedResult.results.map((result, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedResult((prev) => (prev ? { ...prev, selectedIndex: index } : prev))}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        background: selectedResult.selectedIndex === index ? '#e6f7ff' : 'transparent',
                        borderLeft: selectedResult.selectedIndex === index ? '3px solid #1890ff' : '3px solid transparent',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                      onMouseEnter={(e) => {
                        if (selectedResult.selectedIndex !== index) {
                          e.currentTarget.style.background = '#f5f5f5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedResult.selectedIndex !== index) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <span style={{ 
                        flex: 1, 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13
                      }}>
                        {result.name}
                      </span>
                      {result.success ? (
                        <Tag color="success" style={{ margin: 0, fontSize: 11 }}>✓</Tag>
                      ) : (
                        <Tag color="error" style={{ margin: 0, fontSize: 11 }}>✗</Tag>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ 
                  padding: '12px 16px', 
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <Button 
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadAllPDB(selectedResult.results, selectedResult.title)}
                    block
                  >
                    下载所有结果
                  </Button>
                </div>
              </div>
            )}

            {/* 右侧内容区域 */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              overflow: 'hidden',
              minWidth: 0
            }}>
              {/* 单序列时的下载按钮 */}
              {selectedResult.results.length === 1 && (
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => downloadAllPDB(selectedResult.results, selectedResult.title)}
                  >
                    下载所有结果
                  </Button>
                </div>
              )}

              {selectedResult.results[selectedResult.selectedIndex] && (() => {
                const current = selectedResult.results[selectedResult.selectedIndex];
                const counts = countAtomsResidues(current.pdb_content);
                const residues = current.residues && current.residues > 0 ? current.residues : counts.residues;
                const atoms = current.atoms && current.atoms > 0 ? current.atoms : counts.atoms;
                return (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* 信息标签 */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                      <Space wrap>
                        <Tag color="blue">序列: {current.name}</Tag>
                        <Tag color="green">残基数: {residues}</Tag>
                        <Tag color="orange">原子数: {atoms}</Tag>
                      </Space>
                    </div>

                    {/* 3D 结构查看器 */}
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <MolStarViewer
                        key={`molstar-${selectedResult.selectedIndex}-${current.name}`}
                        pdbData={current.pdb_content}
                        title={`${current.name} - 预测结构`}
                        height="auto"
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
